"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

type Step = "code" | "role" | "musician";

type ActiveSession = {
  sessionId: string;
  setlistName: string | null;
  code: string;
};

export default function JoinPage() {
  const router = useRouter();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [musicians, setMusicians] = useState<string[]>([]);
  const [step, setStep] = useState<Step>("code");
  const [conflictSession, setConflictSession] = useState<ActiveSession | null>(null);

  async function doLookup() {
    const trimmed = code.trim().toUpperCase();
    setLoading(true);
    setError(null);

    const res = await fetch(`/api/sessions/lookup?code=${trimmed}`);
    if (!res.ok) {
      setError("Code invalide ou session terminée.");
      setLoading(false);
      return;
    }

    const data = await res.json();
    setSessionId(data.sessionId);
    setMusicians(data.musicians ?? []);
    setStep("role");
    setLoading(false);
  }

  async function handleLookup() {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length !== 4) return;

    setLoading(true);
    setError(null);

    try {
      let active: ActiveSession | null = null;
      try {
        const mineRes = await fetch("/api/sessions/mine", { cache: "no-store" });
        if (mineRes.ok) {
          const { data } = await mineRes.json() as { data: ActiveSession | null };
          active = data;
        }
      } catch {
        // Not authenticated or network error — proceed to lookup
      }

      if (active) {
        setConflictSession(active);
        setLoading(false);
        return;
      }

      await doLookup();
    } catch {
      setLoading(false);
    }
  }

  async function handleConfirmLeave() {
    await fetch("/api/sessions/leave", { method: "POST" }).catch(() => {});
    setConflictSession(null);
    await doLookup();
  }

  function handleChooseMusician() {
    if (musicians.length === 0) {
      router.replace(`/live/master?sessionId=${sessionId}`);
    } else {
      setStep("musician");
    }
  }

  function handleIdentify(musicianName: string) {
    router.replace(`/live/master?sessionId=${sessionId}&musician=${encodeURIComponent(musicianName)}`);
  }

  if (step === "musician" && sessionId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
        <div className="w-full max-w-xs space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-bold">Qui es-tu ?</h1>
            <p className="text-sm text-muted-foreground">Code : <span className="font-mono font-bold">{code}</span></p>
          </div>

          <div className="flex flex-col gap-3">
            {musicians.map((name) => (
              <Button
                key={name}
                className="w-full h-12 text-base"
                onClick={() => handleIdentify(name)}
              >
                {name}
              </Button>
            ))}
          </div>

          <button
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setStep("role")}
          >
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  if (step === "role" && sessionId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
        <div className="w-full max-w-xs space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-bold">Quel est ton rôle ?</h1>
            <p className="text-sm text-muted-foreground">Code : <span className="font-mono font-bold">{code}</span></p>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              data-testid="live-join-role-musician"
              className="w-full h-14 text-base"
              onClick={handleChooseMusician}
            >
              Musicien
              <span className="ml-2 text-xs opacity-70">accords + paroles</span>
            </Button>
            <Button
              data-testid="live-join-role-slave"
              variant="outline"
              className="w-full h-14 text-base"
              onClick={() => router.replace(`/live/${sessionId}/slave`)}
            >
              Projecteur
              <span className="ml-2 text-xs opacity-70">paroles uniquement</span>
            </Button>
          </div>

          <button
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setStep("code")}
          >
            ← Changer de code
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
        <div className="w-full max-w-xs space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-bold">Rejoindre une session</h1>
            <p className="text-sm text-muted-foreground">Entre le code affiché sur l&apos;écran du lead</p>
          </div>

          <input
            data-testid="live-join-input"
            type="text"
            value={code}
            onChange={(e) => {
              setError(null);
              setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4));
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            placeholder="XXXX"
            maxLength={4}
            className="w-full text-center text-4xl font-mono font-bold tracking-widest h-20 rounded-xl border-2 border-input bg-transparent focus:outline-none focus:border-primary uppercase"
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
          />

          {error && (
            <p data-testid="live-join-error" className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button
            data-testid="live-join-submit"
            onClick={handleLookup}
            disabled={code.length !== 4 || loading}
            className="w-full h-12 text-base"
          >
            {loading ? "Connexion..." : "Continuer"}
          </Button>
        </div>
      </div>

      <Dialog open={conflictSession !== null} onOpenChange={(open) => { if (!open) setConflictSession(null); }}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Session en cours</DialogTitle>
            <DialogDescription>
              Tu es déjà dans une session{conflictSession?.setlistName ? ` (${conflictSession.setlistName})` : ""}. Quitter pour rejoindre une nouvelle ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button variant="destructive" onClick={handleConfirmLeave}>
              Quitter et rejoindre
            </Button>
            <Button variant="outline" onClick={() => setConflictSession(null)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
